import { useState, useCallback } from 'react';

interface AddressValue {
  zonecode: string;
  roadAddress: string;
  jibunAddress: string;
  detailAddress: string;
}

interface DaumPostcodeSearchProps {
  value?: AddressValue;
  onChange: (value: AddressValue) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
}

export default function DaumPostcodeSearch({
  value,
  onChange,
  placeholder = '주소를 검색해주세요',
  label,
  required,
}: DaumPostcodeSearchProps) {
  const [detailFocused, setDetailFocused] = useState(false);

  const handleSearch = useCallback(() => {
    new window.daum.Postcode({
      oncomplete(data) {
        onChange({
          zonecode: data.zonecode,
          roadAddress: data.roadAddress,
          jibunAddress: data.jibunAddress,
          detailAddress: value?.detailAddress || '',
        });
      },
    }).open();
  }, [onChange, value?.detailAddress]);

  const displayAddress = value?.roadAddress || value?.jibunAddress || '';

  return (
    <div>
      {label && (
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}

      {/* 주소 검색 버튼 + 결과 */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSearch}
          className="flex items-center gap-1.5 px-4 py-3 rounded-xl text-sm font-semibold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0"
          style={{ backgroundColor: '#2E7D6B' }}
        >
          <i className="ri-search-line" />
          주소 찾기
        </button>
        <div className="flex-1 relative">
          <input
            type="text"
            value={displayAddress}
            readOnly
            placeholder={placeholder}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 text-gray-700 outline-none cursor-pointer"
            onClick={handleSearch}
          />
          {displayAddress && (
            <button
              type="button"
              onClick={() => onChange({ zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-5 h-5 flex items-center justify-center"
            >
              <i className="ri-close-circle-line" />
            </button>
          )}
        </div>
      </div>

      {/* 우편번호 */}
      {value?.zonecode && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-400 flex-shrink-0">우편번호</span>
          <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{value.zonecode}</span>
        </div>
      )}

      {/* 상세주소 입력 */}
      {displayAddress && (
        <input
          type="text"
          value={value?.detailAddress || ''}
          onChange={(e) => onChange({ ...(value || { zonecode: '', roadAddress: '', jibunAddress: '' }), detailAddress: e.target.value })}
          onFocus={() => setDetailFocused(true)}
          onBlur={() => setDetailFocused(false)}
          placeholder={detailFocused || value?.detailAddress ? '상세주소를 입력하세요 (예: 101동 202호)' : ''}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white mt-2"
        />
      )}
    </div>
  );
}